import { useStore } from "zustand";
import { getSelectedKeyMessagesWithLabels } from "@/components/news-feed/utils/filter-lookups";
import { derive } from "@/lib/derive-zustand";
import useNewsFeedParamsStore from "@/store/news-feed/params/newsFeed-params.slice";

/** Minimal filter shape for getSelectedFilters (e.g. create key message modal). */
interface FilterWithSelectedOptions {
  selectedOptions: string[];
}

import useKeyMessageStore from "./key-message.slice";

export type KeyMessageFilterItem = {
  id: string;
  value: string;
  count: number;
  selected: boolean;
};

const deriveKeyMessageFilterItems = derive<KeyMessageFilterItem[]>((get) => {
  const { keyMessageGroups } = get(useKeyMessageStore);
  const { selectedFilters } = get(useNewsFeedParamsStore);
  const selectedKeyMessageIds = new Set(
    selectedFilters.get("KEY_MESSAGE") || [],
  );

  return keyMessageGroups.flatMap(({ keyMessageItems }) =>
    keyMessageItems.map((item) => ({
      id: item.id,
      value: item.name,
      // Use total if available, otherwise 0 for display
      // The total should be preserved in state updates, not defaulted here
      count: item.total ?? 0,
      selected: selectedKeyMessageIds.has(item.id),
    })),
  );
});

export const useGetKeyMessageFilterItems = (
  searchTerm: string,
): KeyMessageFilterItem[] => {
  const items = useStore(deriveKeyMessageFilterItems);
  return searchTerm
    ? items.filter((item) =>
        item.value.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : items;
};

/**
 * @param name
 * @returns {string}
 *
 * if key message name exceeds 3 words, it will be trimmed to 3 words
 */
export const trimKeyMessageName = (name: string): string => {
  const maxLength = 3;
  const words = name.trim().split(" ");

  if (words.length > maxLength) {
    return "";
  }

  // Remove punctuation from each word and join
  return words
    .map((word) => word.replace(/[.,/#!$%^&*;:{}=\-_`~()'"`]/g, ""))
    .join(" ");
};

/**
 * @param filters
 * @returns {string}
 *
 * get selected filters for create key message modal
 */
export const getSelectedFilters = (
  filters: FilterWithSelectedOptions[],
): string => {
  return filters.flatMap(({ selectedOptions }) => selectedOptions).join(", ");
};

// Removed: updateSelectedKeyMessageFilterExclusion is now part of the slice actions

/**
 * Selector hook that gets selected key messages with their labels and loading states.
 * Returns an object with selected items with human-readable labels.
 */
export const useSelectedKeyMessagesWithLabels = () => {
  const selectedFilters = useNewsFeedParamsStore(
    (state) => state.selectedFilters,
  );
  const { keyMessages, isFetchingKeyMessagesAndGroups } =
    useKeyMessageStore.getState();

  const selectedKeyMessageIds = selectedFilters.get("KEY_MESSAGE") || [];
  const labels: string[] = [];

  // Convert each selected ID to its label
  for (const id of selectedKeyMessageIds) {
    const keyMessage = keyMessages.find((km) => km.id === id);
    if (keyMessage) {
      labels.push(keyMessage.name);
    } else {
      labels.push(`Key Topic ${id}`);
    }
  }

  return {
    selected: labels,
    isLoading: isFetchingKeyMessagesAndGroups,
  };
};

/**
 * Derives selected key messages with their labels.
 * Returns a map where "KEY_MESSAGE" contains selected items with human-readable labels.
 * This follows the same pattern as static and dynamic filter selectors for consistency.
 */
const deriveSelectedKeyMessagesWithLabels = derive((get) => {
  const { selectedFilters } = get(useNewsFeedParamsStore);
  const keyMessageState = get(useKeyMessageStore);

  const selectedKeyMessageIds = selectedFilters.get("KEY_MESSAGE") || [];

  return getSelectedKeyMessagesWithLabels(
    selectedKeyMessageIds,
    keyMessageState,
  );
});

/**
 * Selector hook that gets selected key messages with their labels.
 * Returns a map where "KEY_MESSAGE" contains selected items with human-readable labels.
 * This follows the same pattern as useSelectedStaticFiltersWithLabels and useSelectedDynamicFiltersWithLabels.
 */
export const useSelectedKeyMessagesWithLabelsMap = () =>
  useStore(deriveSelectedKeyMessagesWithLabels) as Map<"KEY_MESSAGE", string[]>;
